import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  Clock,
  Copy,
  Download,
  Loader2,
  Scale,
  User
} from 'lucide-react';
import { useAppSelector } from '../../redux/store';
import { messageSelectOneObjectByQuery } from '../../redux/thunks/DBServiceThunks';
import RelevantDocuments from './RelevantDocuments';
import MessageContentFormatter from './MessageContentFormatter';
import { getLanguageDirection } from '../../i18n/config';
import { Card } from '../ui/card';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useProxyDocumentUrlMutation } from '../../common/backend-api/backend-api';
import { downloadDocumentsAsZip } from '../../utils/bulk-document-download';


interface MessageProps {
  messageId: string;
}

/**
 * Premium message component for displaying chat messages
 */
const Message: React.FC<MessageProps> = ({ messageId }) => {
  const { t, i18n } = useTranslation();
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const messageTextRef = useRef<HTMLDivElement>(null);
  const isRTL = getLanguageDirection(i18n.language) === 'rtl';

  // Get proxy document mutation
  const [proxyDocumentUrl] = useProxyDocumentUrlMutation();

  // Use memoized selectors
  const message = useAppSelector(state => messageSelectOneObjectByQuery(state.DBMessageReducer, { _id: messageId }));

  // Return null if message not found
  if (!message) {
    return null;
  }

  const { sender, content, updatedAt } = message;
  const isUserMessage = sender === 'user';

  // Format time for display
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const twentyFourHoursMs = 24 * 60 * 60 * 1000;
    const isOlderThan24Hours = now.getTime() - date.getTime() > twentyFourHoursMs;

    if (isOlderThan24Hours) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      return `${day}/${month}/${year} ${time}`;
    }

    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Function to copy the rendered text
  const copyRenderedText = () => {
    if (messageTextRef.current) {
      const renderedText = messageTextRef.current.innerText;
      navigator.clipboard.writeText(renderedText);
    }
  };

  // Function to handle downloading all sources
  const handleDownloadAllSources = async (format: 'pdf' | 'word') => {
    if (!message.relevantDocumentIds || Object.keys(message.relevantDocumentIds).length === 0) {
      alert(t('components.message.downloadSourcesEmpty', 'No sources available to download'));
      return;
    }

    // Extract document IDs from the Record
    const documentIds = Object.keys(message.relevantDocumentIds);

    setIsDownloadingAll(true);
    setDownloadProgress({ current: 0, total: documentIds.length });

    try {
      // Create a map of document IDs to titles (we'll need to get these from Redux or props)
      // For now, we'll use the documentId as the title
      const documentTitles = new Map<string, string>();
      documentIds.forEach((id, index) => {
        documentTitles.set(id, `Document_${index + 1}`);
      });

      const result = await downloadDocumentsAsZip(
        {
          documentIds: documentIds,
          format,
          zipFilename: `sources_${messageId}_${format}.zip`,
          onProgress: (current, total) => {
            setDownloadProgress({ current, total });
          },
          onError: (documentId, error) => {
            console.error(`Failed to download document ${documentId}:`, error);
          }
        },
        proxyDocumentUrl,
        documentTitles
      );

      // Show success message
      alert(t('components.message.downloadSourcesSuccess', `Downloaded {{count}} sources successfully`, {
        count: result.successCount
      }));

      // If some failed, show warning
      if (result.failedCount > 0) {
        console.warn(`${result.failedCount} documents failed to download:`, result.errors);
      }

    } catch (error: any) {
      console.error('Failed to download sources:', error);
      alert(t('components.message.downloadSourcesError', 'Failed to download sources. Please try again.'));
    } finally {
      setIsDownloadingAll(false);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div
      data-message-id={messageId}
      className={cn(
        'flex mb-6 max-w-full',
        isUserMessage ? 'justify-end' : 'justify-start'
      )}
    >
      {/* Assistant Avatar (only shown for assistant messages) */}
      {!isUserMessage && (
        <Avatar className="w-10 h-10 mr-3 shadow-sm">
          <AvatarFallback className="bg-primary text-primary-foreground">
            <Scale size={20} />
          </AvatarFallback>
        </Avatar>
      )}

      {/* Message Content */}
      <Card
        dir={getLanguageDirection(i18n.language)}
        className={cn(
          'px-6 py-4 mb-4 text-start max-w-[75%] overflow-hidden relative transition-smooth',
          isUserMessage
            ? 'rounded-2xl rounded-tr-sm bg-primary text-primary-foreground shadow-md border-0'
            : 'rounded-2xl rounded-tl-sm bg-card text-card-foreground shadow-sm border'
        )}
      >
        {/* Message text with proper typography and citations */}
        <div
          ref={messageTextRef}
          className={cn(
            'whitespace-pre-wrap leading-relaxed font-normal text-[0.95rem]',
            isUserMessage ? 'text-primary-foreground' : 'text-foreground'
          )}
        >
          <MessageContentFormatter
            content={content}
            isUserMessage={isUserMessage}
          />
        </div>

        {/* Relevant Documents (only for assistant messages with relevantDocumentIds) */}
        {!isUserMessage && message.relevantDocumentIds && (
          <RelevantDocuments relevantDocumentIds={message.relevantDocumentIds} messageId={messageId} />
        )}

        {/* Message footer with timestamp and actions */}
        <div
          className={cn(
            'flex items-center mt-3 pt-2 border-t',
            isUserMessage ? 'justify-end border-primary-foreground/10' : 'justify-start border-border/40'
          )}
        >
          {/* Timestamp */}
          <div
            dir={isRTL ? 'rtl' : 'ltr'}
            className={cn(
              'flex items-center text-[0.7rem] font-medium',
              isUserMessage ? 'text-primary-foreground/70' : 'text-muted-foreground'
            )}
          >
            <Clock size={12} className="mx-1 mb-0.5" />
            {formatTime(updatedAt)}
          </div>

          {/* Action buttons (only for assistant messages) */}
          {!isUserMessage && messageId && (
            <TooltipProvider>
              <div className="flex ml-4 gap-1">


                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={copyRenderedText}
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-gold hover:bg-gold/8"
                    >
                      <Copy size={14} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('components.message.copyTooltip')}</p>
                  </TooltipContent>
                </Tooltip>

                {/* Download All Sources button */}
                {message.relevantDocumentIds && Object.keys(message.relevantDocumentIds).length > 0 && (
                  <DropdownMenu>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="xs"
                            disabled={isDownloadingAll}
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-gold hover:bg-gold/8"
                          >
                            {isDownloadingAll ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Download size={14} />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>
                          {isDownloadingAll
                            ? t('components.message.downloadingSources', `Downloading {{current}}/{{total}} sources...`, downloadProgress)
                            : t('components.message.downloadAllSources', `Download ${Object.keys(message.relevantDocumentIds).length} sources`)}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => handleDownloadAllSources('pdf')}
                        disabled={isDownloadingAll}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        {t('components.message.downloadAllAsPDF', 'Download All as ZIP (PDF)')}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDownloadAllSources('word')}
                        disabled={isDownloadingAll}
                      >
                        <Download className="mr-2 h-4 w-4" />
                        {t('components.message.downloadAllAsWord', 'Download All as ZIP (Word)')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </TooltipProvider>
          )}
        </div>
      </Card>

      {/* User Avatar (only shown for user messages) */}
      {isUserMessage && (
        <Avatar className="w-10 h-10 ml-3 shadow-sm">
          <AvatarFallback className="bg-muted text-foreground">
            <User size={20} />
          </AvatarFallback>
        </Avatar>
      )}


    </div>
  );
};

export default Message;
